import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getAllRolesQuery,
  getUserRoleAssignmentsQuery,
} from '../../../../api/users-api';
import { RoleMember, UserRole } from '../../../../../types';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
import AvatarChip from '../../../../shared/avatar-chip';
import { pagesConfig } from '../../../../shared/pages-config';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import ChevronRight from '@mui/icons-material/ChevronRight';

interface UserRolesProps {
  userEmail: string;
}

export interface UserRoleRow extends RoleMember {
  id: string;
  source: 'Direct' | 'Group';
  grantedVia: string;
  groupName: string;
  roleType: UserRole['roleType'] | '';
  default: boolean;
  desc: string;
  limitedVisEnabled: boolean;
  createdDate: number;
  lastUpdated: number;
  lastUpdatingUser: string | null;
}

export function buildUserRoleRows(
  assignments: RoleMember[] | undefined,
  roles: UserRole[] | undefined
): UserRoleRow[] {
  if (!assignments) return [];

  return assignments.map((assignment) => {
    const role = roles?.find(
      (currentRole) => currentRole.roleName === assignment.role
    );
    const isGroup = assignment.memberType === 'GROUP';

    return {
      ...assignment,
      id: `${assignment.userEmail}|${assignment.role}`,
      source: isGroup ? 'Group' : 'Direct',
      grantedVia: isGroup ? assignment.userEmail : '',
      groupName: isGroup ? assignment.groupName || '' : '',
      roleType: role?.roleType || '',
      default: role?.default || false,
      desc: role?.desc || '',
      limitedVisEnabled: role?.limitedVisEnabled || false,
      createdDate: role?.createdDate || 0,
      lastUpdated: role?.lastUpdated || 0,
      lastUpdatingUser: role?.lastUpdatingUser || null,
    };
  });
}

export function getRolePermissionsLink(roleName: string) {
  return `/${pagesConfig.roleManager.link}/${encodeURIComponent(roleName)}/${
    pagesConfig.roleManagerPermissions.link
  }`;
}

export default function UserRoles({ userEmail }: UserRolesProps) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { administrationRoles } = useUserSettings();

  const {
    data: assignments,
    isLoading,
    isFetching,
  } = useQuery(getUserRoleAssignmentsQuery(userEmail));

  const {
    data: roles,
    isLoading: rolesLoading,
    isFetching: rolesFetching,
  } = useQuery(getAllRolesQuery());

  const rows: UserRoleRow[] = useMemo(
    () => buildUserRoleRows(assignments, roles),
    [assignments, roles]
  );

  const canViewRolePermissions = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.EditRole.value
  );

  const columns: GridColDef<UserRoleRow>[] = useMemo(
    () => [
      {
        field: 'role',
        headerName: 'Role Name',
        flex: 1,
        display: 'flex',
        renderCell: (params) => {
          if (!canViewRolePermissions) {
            return (
              <Tooltip
                arrow
                title={Messages.Errors.Permission.Viewing(
                  AdministrationRoles.EditRole.name
                )}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  minWidth={0}
                  aria-disabled="true"
                  sx={{ color: theme.palette.semantic.text.body }}
                >
                  <Typography variant="body2" noWrap color="inherit">
                    {params.row.role}
                  </Typography>
                  <ChevronRight
                    fontSize="small"
                    sx={{
                      flexShrink: 0,
                      color: theme.palette.semantic.icon.disabled,
                    }}
                  />
                </Stack>
              </Tooltip>
            );
          }
          return (
            <Link
              component={RouterLink}
              to={getRolePermissionsLink(params.row.role)}
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
                color: theme.palette.semantic.hyperlink.active,
                '&:hover': { color: theme.palette.semantic.hyperlink.hover },
              }}
            >
              <Typography variant="body2" noWrap color="inherit">
                {params.row.role}
              </Typography>
              <ChevronRight fontSize="small" sx={{ flexShrink: 0 }} />
            </Link>
          );
        },
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 110,
        type: 'singleSelect',
        valueOptions: ['Direct', 'Group'],
      },
      {
        field: 'grantedVia',
        headerName: 'Granted Via',
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          if (params.row.source === 'Direct') {
            return 'Direct assignment';
          }
          return <AvatarChip email={params.row.grantedVia} type="Group" />;
        },
      },
      {
        field: 'groupName',
        headerName: 'Group Name',
        flex: 1,
        valueFormatter: (value: string) => value || 'N/A',
      },
      {
        field: 'roleType',
        headerName: 'Role Type',
        width: 110,
        type: 'singleSelect',
        valueOptions: [
          { label: 'Admin', value: 'admin' },
          { label: 'User', value: 'user' },
        ],
      },
      {
        field: 'default',
        headerName: 'Is Default',
        type: 'boolean',
        width: 120,
      },
      {
        field: 'desc',
        headerName: 'Description',
        flex: 2,
      },
      {
        field: 'isAppAdmin',
        headerName: 'App Admin',
        type: 'boolean',
        width: 120,
      },
      {
        field: 'limitedVisEnabled',
        headerName: 'Limited Visibility',
        type: 'boolean',
        width: 150,
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          if (!value) return null;
          return new Date(value);
        },
        valueFormatter: (value: Date | null) => {
          if (value === null) return 'N/A';
          return value.toLocaleString();
        },
      },
      {
        field: 'lastUpdated',
        headerName: 'Last Updated',
        width: 185,
        type: 'dateTime',
        valueGetter: (value: number) => {
          if (!value) return null;
          return new Date(value);
        },
        valueFormatter: (value: Date | null) => {
          if (value === null) return 'N/A';
          return value.toLocaleString();
        },
      },
      {
        field: 'lastUpdatingUser',
        headerName: 'Last Updated By',
        flex: 2,
        display: 'flex',
        renderCell: (params) => {
          if (!params.row.lastUpdatingUser) return 'N/A';
          return <AvatarChip email={params.row.lastUpdatingUser} type="User" />;
        },
      },
    ],
    [canViewRolePermissions, theme]
  );

  function CustomToolbar() {
    return (
      <SharedToolbar
        quickFilterPlaceholder="Search roles"
        secondaryButtons={
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.UserRoleAssignments, userEmail],
              });
            }}
            tooltipLabel="Refresh"
          />
        }
      />
    );
  }

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }} gap={1}>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Roles', <AdminPanelSettings />),
        }}
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        loading={isLoading || isFetching || rolesLoading || rolesFetching}
        disableRowSelectionOnClick
        gridStateKey="userRolesGridStates"
        defaultGridState={{
          sorting: {
            sortModel: [
              { field: 'source', sort: 'asc' },
              { field: 'role', sort: 'asc' },
            ],
          },
          columns: {
            columnVisibilityModel: {
              isAppAdmin: false,
              limitedVisEnabled: false,
              createdDate: false,
              lastUpdated: false,
              lastUpdatingUser: false,
            },
          },
        }}
      />
    </Stack>
  );
}
